import { StatusPill, formatCurrency } from '@hue/design-system';
import type { Sale } from '../../lib/api.js';
import { PAYMENT_STATE, paymentState, paymentTotals } from './sale-payment-totals.js';

/// A situação do pagamento da venda numa pílula: Pago, Parcialmente pago, Aguardando confirmação,
/// Sem pagamento ou Cancelada (ADR 0059).
export function PaymentStatePill({ sale }: { sale: Sale }) {
  const state = PAYMENT_STATE[paymentState(sale)];
  return <StatusPill tone={state.tone}>{state.label}</StatusPill>;
}

/// "Recebido R$ X · Aguardando R$ Y · Em aberto R$ Z", com a barra do recebido sobre o total.
export function PaymentSummary({
  sale,
  openLabel = 'Em aberto',
  align = 'end',
}: {
  sale: Sale;
  openLabel?: string;
  align?: 'start' | 'end';
}) {
  if (sale.status === 'CANCELED')
    return (
      <p className="text-xs text-content-muted">
        Cancelada
        {sale.appointmentId && sale.confirmedPaymentTotalCents > 0
          ? '; o recebido virou crédito do cliente'
          : ''}
      </p>
    );
  const totals = paymentTotals(sale);
  const share = (cents: number) =>
    `${String(Math.min(100, (cents / Math.max(sale.netTotalCents, 1)) * 100))}%`;
  return (
    <div className={`grid gap-1 ${align === 'end' ? 'sm:justify-items-end' : ''}`}>
      {sale.netTotalCents > 0 ? (
        <span
          aria-hidden="true"
          className="flex h-1.5 w-40 overflow-hidden rounded-full bg-content/10"
        >
          <span className="h-full bg-positive" style={{ width: share(totals.confirmedCents) }} />
          <span className="h-full bg-warning/60" style={{ width: share(totals.awaitingCents) }} />
        </span>
      ) : null}
      <p className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs tabular-nums text-content-muted">
        {totals.confirmedCents > 0 ? (
          <span className="font-semibold text-positive">
            Recebido {formatCurrency(totals.confirmedCents, sale.currency)}
          </span>
        ) : null}
        {totals.awaitingCents > 0 ? (
          <span className="font-semibold text-warning">
            Aguardando {formatCurrency(totals.awaitingCents, sale.currency)}
          </span>
        ) : null}
        {totals.openCents > 0 ? (
          <span>
            {openLabel} {formatCurrency(totals.openCents, sale.currency)}
          </span>
        ) : (
          <span>Pago integralmente</span>
        )}
      </p>
    </div>
  );
}
